"use client";

import type { ReactNode } from "react";
import Link from "next/link";
import { ArrowRight, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * /my 통합 대시보드의 "탭별 우측 맥락 카드" 프리미티브 (JCG-2073).
 *
 * 각 탭(공부 진행상황·오답노트·…)이 우측 사이드바(customRightSlot)에 portal 로 그리는 맥락
 * 카드를 일관된 모양으로 묶는다. 데스크탑 셸에서는 우측 컬럼에, 모바일/클래식에서는 본문 하단에
 * 동일한 카드로 렌더된다. 디자인 토큰(border/card/foreground/muted-foreground)만 써 다크모드 자동,
 * 브랜드 액센트는 솔리드 `amber-500`(light/dark 공통).
 */
export function MyAside({
  icon: Icon,
  title,
  children,
  className,
}: {
  icon: LucideIcon;
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "rounded-xl border border-border bg-card p-4 text-[13px] leading-relaxed text-muted-foreground",
        className,
      )}
    >
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-[18px] w-[18px] shrink-0 text-amber-500" aria-hidden />
        <h2 className="text-sm font-bold text-foreground">{title}</h2>
      </div>
      <div className="space-y-2.5">{children}</div>
    </section>
  );
}

/** 라벨 ↔ 값 한 줄(값은 앰버 강조, 숫자는 tabular-nums). */
export function MyAsideStat({
  label,
  value,
  accent = true,
}: {
  label: string;
  value: ReactNode;
  /** 값에 앰버 강조를 줄지(기본 true) */
  accent?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span
        className={cn(
          "shrink-0 font-bold tabular-nums",
          accent ? "text-amber-600 dark:text-amber-400" : "text-foreground",
        )}
      >
        {value}
      </span>
    </div>
  );
}

/** 카드 하단 CTA — 화살표가 붙은 강조 링크 행. */
export function MyAsideCta({
  href,
  children,
  external = false,
}: {
  href: string;
  children: ReactNode;
  external?: boolean;
}) {
  const className =
    "mt-1 flex items-center justify-between gap-2 rounded-lg border border-border bg-background px-3 py-2 text-[13px] font-bold text-foreground transition-colors hover:border-amber-300 hover:text-amber-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:hover:border-amber-700 dark:hover:text-amber-300";

  const body = (
    <>
      <span className="truncate">{children}</span>
      <ArrowRight className="h-4 w-4 shrink-0 text-amber-500" aria-hidden />
    </>
  );

  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
        {body}
      </a>
    );
  }
  return (
    <Link href={href} className={className}>
      {body}
    </Link>
  );
}
